import "./Graph.css";
import { useTranslation } from "react-i18next";
import { Content } from "../../../commons/Template/Template";
import { useEffect, useState } from "react";
import { FormLoader } from "../../../commons/Form/Form";
import {
  GraphContainer,
  GraphControls,
  GraphHeader,
  GraphHeaderLabels,
} from "../../../Financing/Loans/Loans";
import { Input } from "../../../commons/Form/Form";
import { useTreasury } from "../../Common/TreasuryContext";
import {
  BarChart,
  BarPlot,
  ChartsGrid,
  ChartsLegend,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  ChartDataProvider,
  ChartsSurface,
} from "@mui/x-charts";
import { formatAmount, formatDate } from "../../../../format";
import { i18n } from "../../../../i18n";
import { capitalize } from "../../../../utils";
import { EmptyGraph } from "../../../commons/EmptySection/EmptySection";
import { useAuth } from "../../../../auth/AuthContext";
import { setDate } from "date-fns";

export default function Graph() {
  const { t } = useTranslation(["common", "treasury"]);
  document.title = t("treasury:graph.title");

  const treasuryOption = {
    label: t("treasury:graph.graph.radio.treasury"),
    value: "treasury",
  };
  const loansOption = {
    label: t("treasury:graph.graph.radio.loans"),
    value: "loans",
  };
  const options = [treasuryOption, loansOption];
  const [selectedOption, setSelectedOption] = useState(treasuryOption);

  const graphControlInput = {
    type: "radio",
    value: selectedOption,
    onChange: setSelectedOption,
    options: options,
  };

  const charts = {
    loans: <LoansGraph />,
    treasury: <TreasuryGraph />,
  };

  return (
    <Content className="TreasuryGraphs">
      <GraphContainer>
        <GraphHeader>
          <GraphHeaderLabels
            heading={t("treasury:graph.graph.heading")}
            subheading={t("treasury:graph.graph.subheading")}
          />
          <GraphControls>
            <Input {...graphControlInput} rowRadio />
          </GraphControls>
        </GraphHeader>
        {charts[selectedOption.value]}
      </GraphContainer>
    </Content>
  );
}

function TreasuryGraph() {
  const { t } = useTranslation(["common", "treasury"]);
  const {
    monthlyCashFlows,
    dailyBalances,
    init: initTreasuryContext,
    loading: loadingTreasuryContext,
  } = useTreasury();

  // Re-init when component unmounts (in the return clean up fn) because of potential previous filter set
  useEffect(() => {
    return () => {
      initTreasuryContext();
    };
  }, []);

  const { activeCompany, loadingActiveCompany } = useAuth();

  const [loading, setLoading] = useState(true);

  const initialData = {
    seriesData: {
      monthlyCashIn: [],
      monthlyCashOut: [],
      dailyBalances: [],
    },
    allData: [],
    monthlyXAxis: [],
    dailyXAxis: [],
    xMin: "",
    xMax: "",
    yMin: "",
    yMax: "",
  };

  const [data, setData] = useState(initialData);

  const handleDataRefresh = () => {
    setLoading(true);

    const seriesData = {
      monthlyCashIn: Object.values(monthlyCashFlows).map(
        (flow) => flow.cash_in,
      ),
      monthlyCashOut: Object.values(monthlyCashFlows).map(
        (flow) => flow.cash_out,
      ),
      dailyBalances: Object.values(dailyBalances),
    };

    const allData = [
      ...seriesData.monthlyCashIn,
      ...seriesData.monthlyCashOut,
      ...seriesData.dailyBalances,
    ];

    const monthlyXAxis = Object.keys(monthlyCashFlows).map((date) =>
      capitalize(formatDate(date, i18n.resolvedLanguage, "year-shortMonth")),
    );
    const dailyXAxis = Object.keys(dailyBalances).map((date) => new Date(date));

    let xMin,
      xMax,
      yMin,
      yMax = 0;
    const dailyXTicks = Object.keys(dailyBalances).map(
      (date) => new Date(date),
    );
    const monthlyXTicks = Object.keys(monthlyCashFlows).map(
      (date) => new Date(date),
    );
    xMin = Math.min(...dailyXTicks, ...monthlyXTicks);
    xMin = setDate(xMin, 1); // corrects potential graph shift if monthly data is present but empty
    xMax = Math.max(...dailyXTicks, ...monthlyXTicks);
    yMin = Math.min(...allData);
    yMin -= Math.abs(yMin) * 0.1; // not multiplying because we don't know the sign
    yMin = Math.min(yMin, 0);
    yMax = Math.max(...allData) * 1.1;

    const newData = {
      seriesData,
      allData,
      monthlyXAxis,
      dailyXAxis,
      xMin,
      xMax,
      yMin,
      yMax,
    };

    setData(newData);
    setLoading(false);
  };

  const lineChartColor = "var(--color-leano-1)";

  const valueFormatter = (value) =>
    formatAmount(value, i18n.resolvedLanguage, activeCompany.currency, true);

  useEffect(() => {
    if (!loadingActiveCompany && !loadingTreasuryContext) handleDataRefresh();
  }, [
    loadingActiveCompany,
    loadingTreasuryContext,
    monthlyCashFlows,
    dailyBalances,
  ]);

  const noData = data.allData.every((value) => !value);

  if (loading) return <GraphLoader />;
  return (
    <div>
      <ChartDataProvider
        series={[
          {
            type: "bar",
            data: data.seriesData.monthlyCashIn,
            label: t("treasury:graph.graph.labels.cash_in"),
            xAxisId: "monthly-x-axis",
            yAxisId: "monthly-y-axis",
            color: "var(--color-TreasuryGraphs-1)",
            valueFormatter: valueFormatter,
          },
          {
            type: "bar",
            data: data.seriesData.monthlyCashOut,
            label: t("treasury:graph.graph.labels.cash_out"),
            xAxisId: "monthly-x-axis",
            yAxisId: "monthly-y-axis",
            color: "var(--color-TreasuryGraphs-2)",
            valueFormatter: valueFormatter,
          },
          {
            type: "line",
            data: data.seriesData.dailyBalances,
            label: t("treasury:graph.graph.labels.dailyBalances"),
            xAxisId: "daily-x-axis",
            yAxisId: "daily-y-axis",
            color: lineChartColor,
            valueFormatter: valueFormatter,
          },
        ]}
        xAxis={[
          {
            id: "monthly-x-axis",
            data: data.monthlyXAxis,
            scaleType: "band",
          },
          {
            id: "daily-x-axis",
            data: data.dailyXAxis,
            scaleType: "time",
            valueFormatter: (date) =>
              formatDate(
                date.toISOString().split("T")[0],
                i18n.resolvedLanguage,
                "day-month",
              ),
            min: data.xMin,
            max: data.xMax,
          },
        ]}
        yAxis={[
          {
            id: "monthly-y-axis",
            min: data.yMin,
            max: data.yMax,
            valueFormatter: valueFormatter,
            width: 7 * 16,
          },
          {
            id: "daily-y-axis",
            min: data.yMin,
            max: data.yMax,
            width: 7 * 16,
          },
        ]}
        height={400}
        margin={{ left: -10, top: 40 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <ChartsLegend />
          <div style={{ width: "15rem" }}>
            <AccountFilterInput />
          </div>
        </div>

        {noData ? (
          <EmptyGraph />
        ) : (
          <ChartsSurface>
            <ChartsGrid horizontal />
            <BarPlot borderRadius={10} className="TreasuryGraphs" />
            <LinePlot className="TreasuryGraphs" />
            <ChartsXAxis position="bottom" axisId="monthly-x-axis" />
            <ChartsYAxis
              axisId="monthly-y-axis"
              position="left"
              labelStyle={{
                transform: "translate(-80px, 0) rotate(-90deg)",
                transformOrigin: "left",
              }}
            />
            <ChartsTooltip className="TreasuryGraphs" />
          </ChartsSurface>
        )}
      </ChartDataProvider>
    </div>
  );
}

function AccountFilterInput() {
  const { t } = useTranslation(["common", "treasury"]);
  const { loading, accounts, dispatchAccountFilter, accountFilter } =
    useTreasury();

  const handleFilterChange = (newFilter) => {
    // setLocalFilter(newFilter);
    newFilter
      ? dispatchAccountFilter({ type: "add", accountFilter: newFilter })
      : dispatchAccountFilter({ type: "clear" });
  };

  const input = {
    type: "select",
    placeholder: t("treasury:graph.graph.select"),
    value: accountFilter,
    onChange: (value) => handleFilterChange(value),
    options:
      !loading &&
      accounts.map((account) => ({
        label: account.name,
        value: account.id,
      })),
    isClearable: true,
    disabled: loading,
  };

  return <Input {...input} />;
}

function LoansGraph() {
  const { t } = useTranslation(["common", "treasury"]);
  const { monthlyCashFlows, loading } = useTreasury();

  const { activeCompany, loadingActiveCompany } = useAuth();

  const getSeries = (dataKey) =>
    Object.values(monthlyCashFlows).map((value) => value[dataKey]);

  const getXAxis = () =>
    Object.keys(monthlyCashFlows).map((date) =>
      capitalize(formatDate(date, i18n.resolvedLanguage, "year-shortMonth")),
    );

  const seriesData = {
    loans_in: !loading && getSeries("loans_in"),
    loans_out: !loading && getSeries("loans_out"),
  };

  const allData = loading
    ? []
    : [...seriesData.loans_in, ...seriesData.loans_out];

  const valueFormatter = (value) =>
    formatAmount(value, i18n.resolvedLanguage, activeCompany.currency);

  if (loadingActiveCompany || loading) return <GraphLoader />;
  if (allData.every((value) => !value)) return <EmptyGraph />;
  return (
    <BarChart
      series={[
        {
          data: seriesData.loans_in,
          valueFormatter: valueFormatter,
          label: t("treasury:graph.graph.labels.loans_in"),
          color: "var(--color-TreasuryGraphs-3)",
        },
        {
          data: seriesData.loans_out,
          valueFormatter: valueFormatter,
          label: t("treasury:graph.graph.labels.loans_out"),
          color: "var(--color-TreasuryGraphs-4)",
        },
      ]}
      height={400}
      xAxis={[
        {
          data: getXAxis(),
          scaleType: "band",
        },
      ]}
      margin={{ left: 40, top: 40 }}
      borderRadius={10}
      yAxis={[
        {
          valueFormatter: (value) =>
            formatAmount(
              value,
              i18n.resolvedLanguage,
              activeCompany.currency,
              true,
            ),
        },
      ]}
      grid={{ horizontal: true }}
      slotProps={{
        legend: {
          direction: "horizontal",
          position: {
            vertical: "top",
            horizontal: "start",
          },
        },
      }}
    >
      <ChartsTooltip className="TreasuryGraphs" />
    </BarChart>
  );
}

function GraphLoader() {
  const { t } = useTranslation(["common"]);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "0.5rem",
        padding: "1rem",
      }}
    >
      <FormLoader />
      <p style={{ textAlign: "center" }}>{t("common:crunchingData")}</p>
    </div>
  );
}
